<template>
    <transition name="pt-message-fade" @before-leave="onClose" @after-leave="$emit('destroy')">
        <div
            v-show="visible"
            :id="id"
            :class="[
                'pt-message',
                type && !iconClass ? `pt-message--${type}` : '',
                center ? 'is-center' : '',
                showClose ? 'is-closable' : '',
                customClass,
            ]"
            :style="customStyle"
            role="alert"
            @mouseenter="clearTimer"
            @mouseleave="startTimer"
        >
            <pt-icon
                v-if="icon || iconClass || defaultIcon"
                :icon="icon || defaultIcon"
                :style="{
                    width: '20px',
                    height: '20px',
                }"
                :class="[typeClass, iconClass]"
            ></pt-icon>
            <slot>
                <p v-if="!dangerouslyUseHTMLString" class="pt-message__content">{{ message }}</p>
                <!-- Caution here, message could've been compromised, never use user's input as message -->
                <!--  eslint-disable-next-line -->
                <p v-else class="pt-message__content" v-html="message"></p>
            </slot>
            <div v-if="showClose" class="pt-message__closeBtn" @click.stop="close">
                <pt-icon
                    icon="pt-icon-clear"
                    :style="{
                        width: '20px',
                        height: '20px',
                    }"
                ></pt-icon>
            </div>
        </div>
    </transition>
</template>
<script lang="ts">
import { defineComponent, computed, ref, PropType, onMounted, onBeforeUnmount } from 'vue';
import ptIcon from '../../icon/index';

// MessageVM is an alias of vue.VNode
import type { MessageVM } from './types';
import { EVENT_CODE } from '../../../utils/aria';
import { on, off } from '../../../utils/dom';
const TypeMap: Indexable<string> = {
    success: 'success',
    info: 'info',
    warning: 'remind',
    error: 'wrong',
};

// 错误类型的默认图标
const iconMap: Indexable<string> = {
    error: 'pt-icon-wrong',
    success: 'pt-icon-Success',
    warning: 'pt-icon-remind',
    info: 'pt-icon-info',
};

export default defineComponent({
    name: 'PtMessage',
    props: {
        customClass: { type: String, default: '' },
        center: { type: Boolean, default: false },
        dangerouslyUseHTMLString: { type: Boolean, default: false },
        duration: { type: Number, default: 3000 },
        icon: { type: String, default: '' },
        iconClass: { type: String, default: '' },
        id: { type: String, default: '' },
        message: {
            type: [String, Object] as PropType<string | MessageVM>,
            default: '',
        },
        onClose: {
            type: Function as PropType<() => void>,
            required: true,
        },
        showClose: { type: Boolean, default: false },
        type: { type: String, default: 'info' },
        offset: { type: Number, default: 20 },
        zIndex: { type: Number, default: 0 },
    },
    emits: ['destroy'],
    setup(props) {
        const typeClass = computed(() => {
            const type = props.type;
            return type && TypeMap[type] ? `pt-message__icon pt-icon-${TypeMap[type]}` : '';
        });
        const customStyle = computed(() => {
            return {
                top: `${props.offset}px`,
                zIndex: props.zIndex,
            };
        });

        const visible = ref(false);
        const defaultIcon = ref();
        let timer = null;

        function startTimer() {
            if (props.duration > 0) {
                timer = setTimeout(() => {
                    if (visible.value) {
                        close();
                    }
                }, props.duration);
            }
        }

        function clearTimer() {
            clearTimeout(timer);
            timer = null;
        }

        function close() {
            visible.value = false;
        }

        function keydown({ code }: KeyboardEvent) {
            if (code === EVENT_CODE.esc) {
                // press esc to close the message
                if (visible.value) {
                    close();
                }
            } else {
                startTimer(); // resume timer
            }
        }

        defaultIcon.value = iconMap[props.type] || '';

        onMounted(() => {
            startTimer();
            visible.value = true;
            on(document, 'keydown', keydown);
        });

        onBeforeUnmount(() => {
            off(document, 'keydown', keydown);
        });

        return {
            typeClass,
            customStyle,
            visible,
            defaultIcon,

            close,
            clearTimer,
            startTimer,
        };
    },
    components: {
        ptIcon,
    },
});
</script>

<style lang="scss" src="../style/message.scss"></style>
